/**
 * React bindings of the Publish workspace and the Export dialog (W2-09 publish-import): translation through
 * `services.i18n`, the open project's DocStore (following project open / close), a version that moves on every
 * document change, the checker's problems, the export preferences and the publish session.
 */

import { useCallback, useEffect, useMemo, useReducer, useState, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Problem } from '../../core/check/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { DocStore } from '../../state/types';
import { checkRunning } from './checkerLink';
import { publishSession, readPrefs, type ExportPrefs } from './prefs';
export { useServicesStore as useStore } from '../../app/registry/react';

export interface Text {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

export function useText(services: Pick<AppServices, 'i18n'>): Text {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(
    () => ({
      locale,
      t: (key, params) => i18n.t(key, params),
      tr: (ref) => i18n.tr(ref),
    }),
    [i18n, locale],
  );
}

/** Moves on every committed change, undo, redo and load (live gesture updates are not counted). */
export function useDocVersion(store: DocStore | null): number {
  const [version, bump] = useReducer((v: number) => v + 1, 0);
  useEffect(() => {
    if (store === null) return undefined;
    return store.subscribe((cs) => {
      if (cs.source !== 'gesture') bump();
    });
  }, [store]);
  return version;
}

export interface ProblemsView {
  readonly problems: readonly Problem[];
  readonly counts: Readonly<Record<'fatal' | 'error' | 'warning' | 'tip', number>>;
  /** A checker pass has not finished yet: problems only the checker finds may still be missing. */
  readonly checking: boolean;
}

/** The checker's problems, counts and pass status, live. */
export function useProblems(services: Pick<AppServices, 'problems'>): ProblemsView {
  const { problems } = services;
  const [version, bump] = useReducer((v: number) => v + 1, 0);
  useEffect(() => problems.subscribe(bump), [problems]);
  const list = problems.problems();
  const counts = problems.counts();
  const checking = checkRunning(problems);
  return useMemo(() => ({ problems: list, counts, checking, version }), [list, counts, checking, version]);
}

/** The export preferences, live. */
export function usePrefs(services: Pick<AppServices, 'settings'>): ExportPrefs {
  const { settings } = services;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      settings.subscribe(() => {
        cb();
      }),
    [settings],
  );
  const get = useCallback((): string => JSON.stringify(readPrefs(settings)), [settings]);
  const snap = useSyncExternalStore(subscribe, get, get);
  return useMemo(() => JSON.parse(snap) as ExportPrefs, [snap]);
}

/** Re-renders on publish session changes (RPE version override, focus requests). */
export function useSession(): number {
  const subscribe = useCallback((cb: () => void): Unsubscribe => publishSession.subscribe(cb), []);
  const get = useCallback((): number => publishSession.revision, []);
  return useSyncExternalStore(subscribe, get, get);
}

/**
 * A value computed after the first paint (heavy work that should not delay showing a panel or dialog). `compute`
 * must be memoised by the caller (useCallback with what it reads): a new function starts a new computation.
 */
export function useDeferred<T>(compute: () => T): T | null {
  const [value, setValue] = useState<{ readonly v: T; readonly from: () => T } | null>(null);
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {
      if (!alive) return;
      try {
        setValue({ v: compute(), from: compute });
      } catch {
        setValue(null);
      }
    }, 0);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [compute]);
  return value !== null && value.from === compute ? value.v : null;
}
